import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Link,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  LocalOffer as TagIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AutoAwesome as AutoAwesomeIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { getConfigSync } from '../services/config';
import { getAccessToken } from '../services/auth';

// Format duration in mm:ss
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Format time
function formatTime(timestamp) {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Direction badge
function DirectionBadge({ direction }) {
  const colors = {
    inbound: 'success',
    outbound: 'primary',
    internal: 'default',
  };
  return (
    <Chip
      label={direction}
      size="small"
      color={colors[direction] || 'default'}
      sx={{ ml: 1 }}
    />
  );
}

// Tag type options
const TAG_TYPES = [
  { value: 'new_ticket', label: 'New Ticket' },
  { value: 'ticket_follow_up', label: 'Ticket Follow Up' },
  { value: 'returning_tech_call', label: 'Returning Tech Call' },
];

// Generate ConnectWise ticket URL using the share link format (more reliable)
const getTicketUrl = (ticketNumber) => {
  try {
    const config = getConfigSync();
    if (!config?.connectwiseUrl || !ticketNumber) return null;

    // Use the share link format if company name is configured (more reliable)
    if (config.connectwiseCompanyName) {
      const baseUrl = config.connectwiseUrl.replace(/\/v4_6_release\/ConnectWise\.aspx.*$/, '');
      const redirectUrl = `services/system_io/Service/fv_sr100_request.rails?service_recid=${ticketNumber}&companyName=${config.connectwiseCompanyName}`;
      return `${baseUrl}/?redirecturl=${encodeURIComponent(redirectUrl)}`;
    }

    // Fallback to old format
    return `${config.connectwiseUrl}?routeTo=ServiceFXSR/${ticketNumber}`;
  } catch {
    return null;
  }
};

// Single call card component
function CallCard({ call, onTag, onTagType, onDismiss, onConfirmSuggestion, tagging, savingTag, confirmingSuggestion, isEnded = false }) {
  const [ticketNumber, setTicketNumber] = useState('');
  const [duration, setDuration] = useState(call.duration_seconds || 0);
  const [selectedTag, setSelectedTag] = useState(call.call_tag || null);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Update duration every second (only for active calls)
  useEffect(() => {
    if (isEnded) {
      // For ended calls, calculate final duration
      if (call.ended_at) {
        const endTime = new Date(call.ended_at).getTime();
        const startTime = new Date(call.started_at).getTime();
        setDuration(Math.floor((endTime - startTime) / 1000));
      }
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - new Date(call.started_at).getTime()) / 1000);
      setDuration(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [call.started_at, call.ended_at, isEnded]);

  // Update selected tag when call.call_tag changes
  useEffect(() => {
    setSelectedTag(call.call_tag || null);
  }, [call.call_tag]);

  // Handle tag type selection - allows changing to different tag
  const handleTagChange = async (tagType) => {
    if (selectedTag === tagType) {
      // Clicking same tag again - do nothing (or could implement unselect)
      return;
    }
    // Allow changing to a different tag
    setSelectedTag(tagType);
    await onTagType(call, tagType);
  };

  const handleTag = async () => {
    if (!ticketNumber.trim()) return;
    await onTag(call, ticketNumber.trim());
    setTicketNumber('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTag();
    }
  };

  return (
    <Paper
      elevation={isEnded ? 1 : 3}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: 4,
        borderColor: isEnded ? 'grey.400' : (call.direction === 'inbound' ? 'success.main' : 'primary.main'),
        backgroundColor: isEnded ? 'grey.50' : 'background.paper',
        opacity: isEnded ? 0.9 : 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {isEnded ? (
            <PhoneIcon color="disabled" sx={{ mr: 1 }} />
          ) : (
            <PhoneInTalkIcon color="primary" sx={{ mr: 1, animation: 'pulse 1.5s infinite' }} />
          )}
          <Typography variant="h6" component="span" color={isEnded ? 'text.secondary' : 'text.primary'}>
            {call.caller_name || call.caller_number}
          </Typography>
          <DirectionBadge direction={call.direction} />
          {isEnded && (
            <Chip label="Ended" size="small" variant="outlined" sx={{ ml: 1 }} />
          )}
        </Box>
        <IconButton size="small" onClick={() => onDismiss(call.call_id)} title="Dismiss">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Caller
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {call.caller_number}
          </Typography>
          {call.caller_name && call.caller_name !== call.caller_number && (
            <Typography variant="body2" color="text.secondary">
              {call.caller_name}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Extension
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {call.extension}{call.extension_name ? ` - ${call.extension_name}` : ''}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Duration
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {formatDuration(duration)}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 80 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Started
          </Typography>
          <Typography variant="body2">
            {formatTime(call.started_at)}
          </Typography>
        </Box>
      </Box>

      {/* Tagged tickets */}
      {call.tickets && call.tickets.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Tagged Tickets
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {call.tickets.map((ticket, idx) => {
              const ticketUrl = getTicketUrl(ticket.ticketNumber);
              return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {ticketUrl ? (
                  <Link
                    href={ticketUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`#${ticket.ticketNumber}`}
                      color="success"
                      size="small"
                      clickable
                      title="Click to open in ConnectWise"
                    />
                  </Link>
                ) : (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`#${ticket.ticketNumber}`}
                    color="success"
                    size="small"
                  />
                )}
                {(ticket.ticketSummary || ticket.ticketCompany) && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {ticket.ticketSummary}
                    {ticket.ticketCompany && (
                      <Typography component="span" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                        ({ticket.ticketCompany})
                      </Typography>
                    )}
                  </Typography>
                )}
              </Box>
            );
            })}
          </Box>
        </Box>
      )}

      {/* Auto-match Suggestions */}
      {call.suggestions && call.suggestions.length > 0 && (!call.tickets || call.tickets.length === 0) && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesomeIcon fontSize="small" color="info" />
            <Typography variant="caption" color="info.main" fontWeight="bold">
              Suggested Tickets ({call.suggestions.length})
            </Typography>
            {call.suggestions[0]?.confidence >= 0.85 && (
              <Tooltip title="High confidence match">
                <VerifiedIcon fontSize="small" color="success" />
              </Tooltip>
            )}
          </Box>
          {showSuggestions && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {call.suggestions.slice(0, 3).map((suggestion, idx) => {
                const ticketUrl = getTicketUrl(suggestion.ticket_number);
                const isHighConfidence = suggestion.confidence >= 0.85;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isHighConfidence ? 'success.light' : 'grey.300',
                    }}
                  >
                    <Chip
                      label={`#${suggestion.ticket_number}`}
                      size="small"
                      variant="outlined"
                      color={isHighConfidence ? 'success' : 'default'}
                      sx={{ minWidth: 80 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {suggestion.ticket_summary && (
                        <Typography variant="body2" noWrap title={suggestion.ticket_summary}>
                          {suggestion.ticket_summary}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {suggestion.company && (
                          <Typography variant="caption" color="primary.main" fontWeight="bold">
                            {suggestion.company}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(suggestion.confidence * 100)}% • {suggestion.reason}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant={isHighConfidence ? 'contained' : 'outlined'}
                      color={isHighConfidence ? 'success' : 'primary'}
                      onClick={() => onConfirmSuggestion(call, suggestion)}
                      disabled={confirmingSuggestion}
                      sx={{ minWidth: 80 }}
                    >
                      {confirmingSuggestion ? <CircularProgress size={16} /> : 'Confirm'}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Ticket tagging input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="CW Ticket #"
          value={ticketNumber}
          onChange={(e) => setTicketNumber(e.target.value.replace(/\D/g, ''))}
          onKeyPress={handleKeyPress}
          disabled={tagging}
          sx={{ width: 150 }}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleTag}
          disabled={!ticketNumber.trim() || tagging}
          startIcon={tagging ? <CircularProgress size={16} /> : null}
        >
          {tagging ? 'Tagging...' : 'Tag'}
        </Button>
      </Box>

      {/* Call Type Tags */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TagIcon fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          Call Type:
        </Typography>
        <FormGroup row sx={{ gap: 0 }}>
          {TAG_TYPES.map((tag) => (
            <FormControlLabel
              key={tag.value}
              control={
                <Checkbox
                  size="small"
                  checked={selectedTag === tag.value}
                  onChange={() => handleTagChange(tag.value)}
                  disabled={savingTag}
                  sx={{ py: 0 }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: selectedTag === tag.value ? 'primary.main' : 'text.secondary',
                    fontWeight: selectedTag === tag.value ? 'bold' : 'normal',
                  }}
                >
                  {tag.label}
                </Typography>
              }
              sx={{ mr: 2 }}
            />
          ))}
        </FormGroup>
        {savingTag && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Box>
    </Paper>
  );
}

// Main CallPop component
export default function CallPop() {
  const [calls, setCalls] = useState([]);
  const [todaysCalls, setTodaysCalls] = useState([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [tagging, setTagging] = useState({});
  const [savingTag, setSavingTag] = useState({});
  const [confirmingSuggestion, setConfirmingSuggestion] = useState({});
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [dismissedCalls, setDismissedCalls] = useState(new Set());

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const audioRef = useRef(null);

  // Get API URL from config (processor API for call pop)
  const getApiUrl = useCallback(() => {
    try {
      const config = getConfigSync();
      // Use processor API URL for call pop SSE stream
      return config.apiUrl || 'https://recordings-api.adnetapps.com';
    } catch (error) {
      console.error('[CallPop] Config not loaded:', error);
      return null;
    }
  }, []);

  // Get hub URL from config (for ticket tagging)
  const getHubUrl = useCallback(() => {
    try {
      return 'https://3cx.adnetcloud.com/api/v1';
    } catch (error) {
      console.error('[CallPop] Hub URL error:', error);
      return null;
    }
  }, []);

  // Get instance ID from config
  const getInstanceId = useCallback(() => {
    try {
      const config = getConfigSync();
      return config.instanceId;
    } catch (error) {
      console.error('[CallPop] Config not loaded:', error);
      return null;
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(console.error);
    }
  }, [soundEnabled]);

  // Connect to SSE stream
  const connect = useCallback(async () => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) {
      setError('Configuration not loaded. Please refresh the page.');
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setConnecting(true);
    setError(null);

    try {
      // Create SSE connection to processor API (no auth needed for SSE)
      const response = await fetch(
        `${apiUrl}/api/call-pop/stream/${instanceId}`,
        {
          headers: {
            'Accept': 'text/event-stream',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Connection failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      setConnected(true);
      setConnecting(false);

      // Read stream
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.substring(6));
                  handleSSEMessage(data);
                } catch (e) {
                  console.error('[CallPop] Failed to parse SSE message:', e);
                }
              }
            }
          }
        } catch (error) {
          console.error('[CallPop] Stream read error:', error);
          handleDisconnect();
        }
      };

      readStream();
    } catch (error) {
      console.error('[CallPop] Connection error:', error);
      setError(error.message);
      setConnected(false);
      setConnecting(false);

      // Schedule reconnect
      reconnectTimeoutRef.current = setTimeout(connect, 5000);
    }
  }, [getApiUrl, getInstanceId]);

  // Handle SSE messages
  const handleSSEMessage = useCallback((message) => {
    console.log('[CallPop] SSE message:', message.type);

    switch (message.type) {
      case 'connected':
        console.log('[CallPop] Connected to call stream');
        break;

      case 'init':
      case 'update':
      case 'calls':
      case 'cleanup':
        // Filter out dismissed calls
        const callsData = message.calls || message.data || [];
        const activeCalls = callsData.filter(
          (call) => !dismissedCalls.has(call.call_id)
        );

        // Check for new calls (only play sound on 'update', not 'init' or 'cleanup')
        if (message.type === 'update') {
          const currentCallIds = new Set(calls.map((c) => c.call_id));
          const newCalls = activeCalls.filter((c) => !currentCallIds.has(c.call_id));

          if (newCalls.length > 0) {
            playNotificationSound();
          }
        }

        // Log cleanup events
        if (message.type === 'cleanup' && message.cleaned > 0) {
          console.log(`[CallPop] Cleaned up ${message.cleaned} stale calls`);
        }

        setCalls(activeCalls);
        break;

      case 'heartbeat':
        // Keep alive - no action needed
        break;

      case 'error':
        setError(message.data || message.message);
        break;

      default:
        console.warn('[CallPop] Unknown message type:', message.type);
    }
  }, [calls, dismissedCalls, playNotificationSound]);

  // Handle disconnect
  const handleDisconnect = useCallback(() => {
    setConnected(false);
    setError('Connection lost. Reconnecting...');

    // Schedule reconnect
    reconnectTimeoutRef.current = setTimeout(connect, 5000);
  }, [connect]);

  // Tag a call with a ticket
  const handleTag = useCallback(async (call, ticketNumber) => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) {
      setError('Configuration not loaded');
      return;
    }

    setTagging((prev) => ({ ...prev, [call.call_id]: true }));

    try {
      // Use the processor API for ticket tagging (no auth required)
      const response = await fetch(`${apiUrl}/api/call-pop/tag-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instanceId: instanceId,
          callId: call.call_id,
          callStartedAt: call.started_at,
          extension: call.extension,
          extensionName: call.extension_name,
          callerNumber: call.caller_number,
          callerName: call.caller_name,
          ticketNumber,
          taggedBy: 'Call Pop User',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error?.message || 'Failed to tag ticket');
      }

      const newTicket = {
        ticketNumber,
        ticketSummary: data.ticket?.ticketSummary || '',
        ticketCompany: data.ticket?.ticketCompany || '',
        taggedBy: 'You',
        taggedAt: data.ticket?.taggedAt || new Date().toISOString(),
      };

      // Update call with new ticket in SSE calls
      setCalls((prev) =>
        prev.map((c) =>
          c.call_id === call.call_id
            ? {
                ...c,
                tickets: [...(c.tickets || []), newTicket],
              }
            : c
        )
      );

      // Update call with new ticket in today's calls
      setTodaysCalls((prev) =>
        prev.map((c) => {
          if (c.call_id === call.call_id) {
            const updatedTickets = [...(c.tickets || []), newTicket];
            return {
              ...c,
              tickets: updatedTickets,
              is_hidden: updatedTickets.length > 0 && c.call_tag !== null,
            };
          }
          return c;
        })
      );

      // Recalculate hidden count
      setTodaysCalls((prev) => {
        const newHidden = prev.filter(c => c.is_hidden).length;
        setHiddenCount(newHidden);
        return prev;
      });

      // Show success feedback
      console.log('[CallPop] Ticket tagged successfully:', data);
    } catch (error) {
      console.error('[CallPop] Tag error:', error);
      setError(error.message);
    } finally {
      setTagging((prev) => ({ ...prev, [call.call_id]: false }));
    }
  }, [getApiUrl, getInstanceId]);

  // Tag a call with a type (New Ticket, Follow Up, etc.)
  const handleTagType = useCallback(async (call, tagType) => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) {
      setError('Configuration not loaded');
      return;
    }

    setSavingTag((prev) => ({ ...prev, [call.call_id]: true }));

    try {
      const response = await fetch(`${apiUrl}/api/call-tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instance_id: instanceId,
          call_id: call.call_id,
          call_started_at: call.started_at,
          extension: call.extension,
          extension_name: call.extension_name,
          caller_number: call.caller_number,
          caller_name: call.caller_name,
          tag_type: tagType,
          tagged_by: 'Call Pop User',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save tag');
      }

      // Update call with new tag in SSE calls
      setCalls((prev) =>
        prev.map((c) =>
          c.call_id === call.call_id
            ? { ...c, call_tag: tagType }
            : c
        )
      );

      // Update call with new tag in today's calls
      setTodaysCalls((prev) =>
        prev.map((c) =>
          c.call_id === call.call_id
            ? { ...c, call_tag: tagType, is_hidden: c.tickets && c.tickets.length > 0 }
            : c
        )
      );

      // Recalculate hidden count
      setTodaysCalls((prev) => {
        const newHidden = prev.filter(c => c.is_hidden).length;
        setHiddenCount(newHidden);
        return prev;
      });

      console.log('[CallPop] Call tag saved successfully:', data);
    } catch (error) {
      console.error('[CallPop] Tag type error:', error);
      setError(error.message);
    } finally {
      setSavingTag((prev) => ({ ...prev, [call.call_id]: false }));
    }
  }, [getApiUrl, getInstanceId]);

  // Dismiss a call
  const handleDismiss = useCallback((callId) => {
    setDismissedCalls((prev) => new Set([...prev, callId]));
    setCalls((prev) => prev.filter((c) => c.call_id !== callId));
  }, []);

  // Confirm an auto-match suggestion
  const handleConfirmSuggestion = useCallback(async (call, suggestion) => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) {
      setError('Configuration not loaded');
      return;
    }

    setConfirmingSuggestion((prev) => ({ ...prev, [call.call_id]: true }));

    try {
      // Use the auto-match confirm endpoint
      const response = await fetch(`${apiUrl}/api/auto-match/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instance_id: instanceId,
          call_id: call.call_id,
          call_started_at: call.started_at,
          caller_number: call.caller_number,
          caller_name: call.caller_name,
          extension: call.extension,
          extension_name: call.extension_name,
          ticket_number: suggestion.ticket_number,
          ticket_summary: suggestion.ticket_summary,
          ticket_company: suggestion.company,
          confidence: suggestion.confidence,
          source: suggestion.source,
          tagged_by: 'Call Pop User (Auto-match)',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to confirm suggestion');
      }

      const newTicket = {
        ticketNumber: suggestion.ticket_number,
        ticketSummary: suggestion.ticket_summary || '',
        ticketCompany: suggestion.company || '',
        taggedBy: 'You (Auto-match)',
        taggedAt: new Date().toISOString(),
      };

      // Update call with new ticket and clear suggestions in SSE calls
      setCalls((prev) =>
        prev.map((c) =>
          c.call_id === call.call_id
            ? {
                ...c,
                tickets: [...(c.tickets || []), newTicket],
                suggestions: [], // Clear suggestions after confirming
              }
            : c
        )
      );

      // Update call with new ticket in today's calls
      setTodaysCalls((prev) =>
        prev.map((c) => {
          if (c.call_id === call.call_id) {
            const updatedTickets = [...(c.tickets || []), newTicket];
            return {
              ...c,
              tickets: updatedTickets,
              suggestions: [],
              is_hidden: updatedTickets.length > 0 && c.call_tag !== null,
            };
          }
          return c;
        })
      );

      console.log('[CallPop] Suggestion confirmed:', suggestion.ticket_number, `(${suggestion.source}, ${Math.round(suggestion.confidence * 100)}%)`);
    } catch (error) {
      console.error('[CallPop] Confirm suggestion error:', error);
      setError(error.message);
    } finally {
      setConfirmingSuggestion((prev) => ({ ...prev, [call.call_id]: false }));
    }
  }, [getApiUrl, getInstanceId]);

  // Fetch today's calls from database (persistent across refreshes)
  const fetchTodaysCalls = useCallback(async () => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();
    if (!apiUrl || !instanceId) return;

    try {
      // Use local date to handle timezone differences with server
      const now = new Date();
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await fetch(`${apiUrl}/api/call-pop/today/${instanceId}?call_date=${localDate}`);
      if (response.ok) {
        const data = await response.json();
        setTodaysCalls(data.calls || []);
        setHiddenCount(data.hidden_calls || 0);
        console.log(`[CallPop] Loaded ${data.total_calls} calls (${data.hidden_calls} hidden)`);
      }
    } catch (err) {
      console.error('[CallPop] Failed to fetch today\'s calls:', err.message);
    }
  }, [getApiUrl, getInstanceId]);

  // Backup polling to catch missed updates (every 10 seconds)
  const pollStatus = useCallback(async () => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();
    if (!apiUrl || !instanceId || !connected) return;

    try {
      const response = await fetch(`${apiUrl}/api/call-pop/status/${instanceId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.calls && data.calls.length > 0) {
          const filteredCalls = data.calls.filter(
            (call) => !dismissedCalls.has(call.call_id)
          );
          setCalls(filteredCalls);
        }
      }
    } catch (err) {
      // Silent fail for backup polling
      console.debug('[CallPop] Backup poll failed:', err.message);
    }
  }, [getApiUrl, getInstanceId, connected, dismissedCalls]);

  // Connect on mount and fetch today's calls
  useEffect(() => {
    connect();
    fetchTodaysCalls();

    // Start backup polling every 10 seconds
    const pollInterval = setInterval(pollStatus, 10000);

    // Refresh today's calls every 30 seconds
    const todaysCallsInterval = setInterval(fetchTodaysCalls, 30000);

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      clearInterval(pollInterval);
      clearInterval(todaysCallsInterval);
    };
  }, [connect, pollStatus, fetchTodaysCalls]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>

      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PhoneIcon fontSize="large" color="primary" />
            <Typography variant="h5">Live Call Pop</Typography>
            <Badge badgeContent={calls.length} color="primary">
              <Chip
                icon={connected ? <LinkIcon /> : <LinkOffIcon />}
                label={connected ? 'Connected' : connecting ? 'Connecting...' : 'Disconnected'}
                color={connected ? 'success' : 'default'}
                variant={connected ? 'filled' : 'outlined'}
              />
            </Badge>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hiddenCount > 0 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {showHidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    <Typography variant="body2">
                      {showHidden ? 'Showing' : 'Hidden'}: {hiddenCount}
                    </Typography>
                  </Box>
                }
                sx={{ mr: 2 }}
              />
            )}
            <Tooltip title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}>
              <IconButton onClick={() => setSoundEnabled(!soundEnabled)}>
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Reconnect">
              <IconButton onClick={connect} disabled={connecting}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Active Calls */}
      {(() => {
        const activeCalls = calls.filter((c) => c.status !== 'ended');
        // Filter today's calls from database based on showHidden toggle
        const visibleTodaysCalls = todaysCalls.filter((c) => showHidden || !c.is_hidden);

        return (
          <>
            {/* Active calls section */}
            {activeCalls.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                <PhoneIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Active Calls
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Incoming and outgoing calls will appear here in real-time.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneInTalkIcon fontSize="small" />
                  Active Calls ({activeCalls.length})
                </Typography>
                {activeCalls.map((call) => (
                  <CallCard
                    key={call.call_id}
                    call={call}
                    onTag={handleTag}
                    onTagType={handleTagType}
                    onDismiss={handleDismiss}
                    onConfirmSuggestion={handleConfirmSuggestion}
                    tagging={tagging[call.call_id]}
                    savingTag={savingTag[call.call_id]}
                    confirmingSuggestion={confirmingSuggestion[call.call_id]}
                  />
                ))}
              </Box>
            )}

            {/* Today's completed calls section (from database - persistent) */}
            {visibleTodaysCalls.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" />
                  Today's Calls ({visibleTodaysCalls.length})
                  {hiddenCount > 0 && !showHidden && (
                    <Chip
                      label={`+${hiddenCount} hidden`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {visibleTodaysCalls.map((call) => (
                  <CallCard
                    key={call.call_id}
                    call={call}
                    onTag={handleTag}
                    onTagType={handleTagType}
                    onDismiss={handleDismiss}
                    onConfirmSuggestion={handleConfirmSuggestion}
                    tagging={tagging[call.call_id]}
                    savingTag={savingTag[call.call_id]}
                    confirmingSuggestion={confirmingSuggestion[call.call_id]}
                    isEnded
                  />
                ))}
              </Box>
            )}
          </>
        );
      })()}

      {/* Connection status footer */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          {connected
            ? 'Polling for calls every 5 seconds'
            : 'Not connected to call stream'}
        </Typography>
      </Box>

      {/* Pulse animation style */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
}
